"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import {
  BookOpen,
  Briefcase,
  CaretDown as ChevronDown,
  CurrencyDollar,
  DeviceMobile,
  Envelope,
  House,
  List as Menu,
  ListNumbers,
  Moon,
  Newspaper,
  Question,
  SignOut,
  Sun,
  Users,
  UsersThree,
} from "@phosphor-icons/react";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

import { MobileNavLink } from "@/components/mobile/MobileNavLink";
import {
  SiteHeaderAuth,
  type ISiteHeaderAuthUser,
} from "@/components/site-header-auth";
import { SocialIconRow } from "@/components/social/SocialIconRow";
import { SiteLogo } from "@/components/site-logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BOOK_A_DEMO_URL } from "@tippo/config";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { LogoutForm, LogoutSubmitCtaButton } from "@/components/auth/logout-button";
import { cn } from "@/lib/utils";
import {
  WaitlistDialog,
  type IWaitlistFoundingTier,
} from "@/components/marketing/WaitlistDialog";

const INFO_LINKS = [
  { href: "/resources", label: "Resources" },
  { href: "/blog", label: "Blog" },
  { href: "/team", label: "Team" },
  { href: "/pricing", label: "Pricing" },
  { href: "/mobile-app", label: "Mobile app" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
] as const;

/** Pricing is surfaced as a top-level desktop link; the rest stay under Info. */
const DESKTOP_INFO_LINKS = INFO_LINKS.filter(({ href }) => href !== "/pricing");

const INFO_LINK_ICONS: Record<
  (typeof INFO_LINKS)[number]["href"],
  React.ReactElement
> = {
  "/resources": <BookOpen weight="duotone" aria-hidden />,
  "/blog": <Newspaper weight="duotone" aria-hidden />,
  "/team": <Users weight="duotone" aria-hidden />,
  "/pricing": <CurrencyDollar weight="duotone" aria-hidden />,
  "/mobile-app": <DeviceMobile weight="duotone" aria-hidden />,
  "/faq": <Question weight="duotone" aria-hidden />,
  "/contact": <Envelope weight="duotone" aria-hidden />,
};

function getInitial(name: string, email: string): string {
  const trimmed = name.trim();
  if (trimmed.length > 0) return trimmed.charAt(0).toUpperCase();
  const first = email.trim().charAt(0);
  return first ? first.toUpperCase() : "?";
}

interface IMarketingNavProps {
  user: ISiteHeaderAuthUser | null;
  portalOpen: boolean;
  foundingTier: IWaitlistFoundingTier | null;
}

export function MarketingNav({
  user,
  portalOpen,
  foundingTier,
}: IMarketingNavProps): React.ReactElement {
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    setMounted(true);
  }, []);

  const showWaitlist = !portalOpen && user == null;

  const employersActive = pathname === "/employers";
  const employeesActive = pathname === "/employees";
  const howItWorksActive = pathname === "/how-it-works";
  const dashboardActive =
    pathname === "/dashboard" || pathname.startsWith("/dashboard/");

  const closeSheet = () => setMobileOpen(false);

  const initial = user ? getInitial(user.name, user.email) : "?";

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75">
      <nav
        className="relative mx-auto flex h-16 max-w-[1240px] items-center px-5 sm:px-7 lg:h-[72px]"
        aria-label="Main navigation"
      >
        <Link href="/" className="shrink-0 flex items-center">
          <SiteLogo />
        </Link>

        {/* Desktop nav — centered so dropdowns anchor to their triggers */}
        <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-full items-center justify-center md:flex">
          <NavigationMenu className="pointer-events-auto z-10 max-w-max flex-none">
            <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/employers"
                  className={cn(
                    "relative inline-flex items-center px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md",
                    pathname === "/employers" && "text-foreground"
                  )}
                >
                  Employer
                  {pathname === "/employers" && (
                    <motion.span
                      layoutId="marketing-nav-active"
                      className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-primary"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 28,
                      }}
                    />
                  )}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/employees"
                  className={cn(
                    "relative inline-flex items-center px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md",
                    pathname === "/employees" && "text-foreground"
                  )}
                >
                  Employee
                  {pathname === "/employees" && (
                    <motion.span
                      layoutId="marketing-nav-active"
                      className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-primary"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 28,
                      }}
                    />
                  )}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/how-it-works"
                  className={cn(
                    "relative inline-flex items-center px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md",
                    howItWorksActive && "text-foreground"
                  )}
                >
                  How it works
                  {howItWorksActive && (
                    <motion.span
                      layoutId="marketing-nav-active"
                      className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-primary"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 28,
                      }}
                    />
                  )}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/pricing"
                  className={cn(
                    "relative inline-flex items-center px-1 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md",
                    pathname === "/pricing" && "text-foreground"
                  )}
                >
                  Pricing
                  {pathname === "/pricing" && (
                    <motion.span
                      layoutId="marketing-nav-active"
                      className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-primary"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 28,
                      }}
                    />
                  )}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      navigationMenuTriggerStyle(),
                      "group bg-transparent"
                    )}
                  >
                    Info
                    <ChevronDown
                      className="relative top-[1px] ml-1 h-3 w-3 transition duration-300 group-data-[state=open]:rotate-180"
                      aria-hidden
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-[200px]">
                  {DESKTOP_INFO_LINKS.map(({ href, label }) => (
                    <DropdownMenuItem key={href} asChild>
                      <Link
                        href={href}
                        className={cn(
                          (pathname === href ||
                            (href === "/blog" && pathname.startsWith("/blog/"))) &&
                            "bg-accent/50 text-accent-foreground"
                        )}
                      >
                        {label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
        </div>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          {/* Auth / waitlist section */}
          <div className="hidden md:flex md:items-center md:gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Toggle theme"
              className="h-9 w-9 rounded-[10px] text-muted-foreground"
              disabled={!mounted}
              onClick={() =>
                mounted && setTheme(theme === "dark" ? "light" : "dark")
              }
            >
              {mounted && theme === "dark" ? (
                <Sun weight="duotone" aria-hidden />
              ) : (
                <Moon weight="duotone" aria-hidden />
              )}
            </Button>
            {showWaitlist ? (
              <WaitlistDialog foundingTier={foundingTier} />
            ) : user != null ? (
              <SiteHeaderAuth user={user} />
            ) : (
              <>
                <Link
                  href="/login"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  Sign in
                </Link>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <a
                    href={BOOK_A_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book a demo
                  </a>
                </Button>
                <Button asChild size="sm" className="whitespace-nowrap">
                  <Link href="/mobile-app">Get the app</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Open menu"
              >
                <motion.div
                  {...(!prefersReducedMotion && {
                    animate: {
                      rotate: mobileOpen ? 90 : 0,
                      scale: mobileOpen ? 1.08 : 1,
                    },
                  })}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="h-5 w-5" />
                </motion.div>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-[min(22rem,85vw)] flex-col gap-0 overflow-hidden p-0"
            >
              <SheetHeader className="shrink-0 border-b px-4 pb-4 pt-4 text-left">
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>

              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
                  <section
                    className="rounded-xl border border-border/80 bg-muted/30 p-3"
                    aria-labelledby="marketing-mobile-nav-heading"
                  >
                    <h2
                      id="marketing-mobile-nav-heading"
                      className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                      Navigation
                    </h2>
                    <nav className="space-y-0.5" aria-label="Mobile marketing">
                      {user != null ? (
                        <MobileNavLink
                          href="/dashboard"
                          active={dashboardActive}
                          onNavigate={closeSheet}
                          icon={<House weight="duotone" />}
                        >
                          Dashboard
                        </MobileNavLink>
                      ) : null}
                      <MobileNavLink
                        href="/employers"
                        active={employersActive}
                        onNavigate={closeSheet}
                        icon={<Briefcase weight="duotone" />}
                      >
                        Employer
                      </MobileNavLink>
                      <MobileNavLink
                        href="/employees"
                        active={employeesActive}
                        onNavigate={closeSheet}
                        icon={<UsersThree weight="duotone" />}
                      >
                        Employee
                      </MobileNavLink>
                      <MobileNavLink
                        href="/how-it-works"
                        active={howItWorksActive}
                        onNavigate={closeSheet}
                        icon={<ListNumbers weight="duotone" />}
                      >
                        How it works
                      </MobileNavLink>
                      <div className="pt-2">
                        <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          Info
                        </p>
                        <div className="space-y-0.5">
                          {INFO_LINKS.map(({ href, label }) => (
                            <MobileNavLink
                              key={href}
                              href={href}
                              active={
                                pathname === href ||
                                (href === "/blog" && pathname.startsWith("/blog/"))
                              }
                              onNavigate={closeSheet}
                              icon={INFO_LINK_ICONS[href]}
                            >
                              {label}
                            </MobileNavLink>
                          ))}
                        </div>
                      </div>
                    </nav>
                  </section>

                  {user != null ? (
                    <>
                      <Separator className="my-4" />
                    </>
                  ) : null}

                  <Separator className="my-4" />

                  <section
                    className="rounded-xl border border-border/80 bg-muted/30 p-3"
                    aria-labelledby="marketing-mobile-theme-heading"
                  >
                    <h2
                      id="marketing-mobile-theme-heading"
                      className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                      Appearance
                    </h2>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        type="button"
                        variant={mounted && theme === "light" ? "secondary" : "outline"}
                        className="justify-center"
                        onClick={() => mounted && setTheme("light")}
                        disabled={!mounted}
                      >
                        <Sun />
                        <span className="sr-only">Light</span>
                      </Button>
                      <Button
                        type="button"
                        variant={mounted && theme === "dark" ? "secondary" : "outline"}
                        className="justify-center"
                        onClick={() => mounted && setTheme("dark")}
                        disabled={!mounted}
                      >
                        <Moon />
                        <span className="sr-only">Dark</span>
                      </Button>
                      <Button
                        type="button"
                        variant={mounted && theme === "system" ? "secondary" : "outline"}
                        className="justify-center"
                        onClick={() => mounted && setTheme("system")}
                        disabled={!mounted}
                      >
                        <span className="text-xs font-semibold">Auto</span>
                      </Button>
                    </div>
                  </section>

                  <Separator className="my-4" />

                  <section
                    className="overflow-hidden rounded-xl border border-border bg-gradient-to-br from-muted/60 via-background to-background p-4 shadow-sm"
                    aria-labelledby="marketing-mobile-account-heading"
                  >
                    <h2
                      id="marketing-mobile-account-heading"
                      className="sr-only"
                    >
                      Your account
                    </h2>
                    {showWaitlist ? (
                      <WaitlistDialog
                        foundingTier={foundingTier}
                        className="w-full"
                      />
                    ) : user != null ? (
                      <>
                        <div className="flex gap-3 items-center">
                          <Avatar className="h-14 w-14 shrink-0 border border-border shadow-sm">
                            <AvatarImage src={user.avatar} alt={user.name} />
                            <AvatarFallback className="bg-primary/15 text-lg font-semibold text-primary">
                              {initial}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1 pt-0.5 items-center flex flex-col h-full">
                            <p className="truncate font-semibold leading-tight text-foreground">
                              {user.name}
                            </p>
                            <p className="mt-1 truncate text-xs text-muted-foreground">
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <Button
                          className="mt-4 w-full"
                          variant="default"
                          asChild
                        >
                          <Link href="/dashboard" onClick={closeSheet}>
                            Go to Dashboard
                          </Link>
                        </Button>
                        <LogoutForm className="mt-2">
                          <LogoutSubmitCtaButton
                            className="w-full gap-2"
                            icon={<SignOut className="h-4 w-4 shrink-0" aria-hidden />}
                          >
                            Log out
                          </LogoutSubmitCtaButton>
                        </LogoutForm>
                      </>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Button asChild>
                          <Link href="/signup" onClick={closeSheet}>
                            Sign up
                          </Link>
                        </Button>
                        <Button asChild variant="outline">
                          <Link href="/login" onClick={closeSheet}>
                            Sign in
                          </Link>
                        </Button>
                        <Button asChild variant="ghost">
                          <a
                            href={BOOK_A_DEMO_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeSheet}
                          >
                            Book a demo
                          </a>
                        </Button>
                      </div>
                    )}
                  </section>
                </div>

                <div className="shrink-0 border-t bg-muted/20 px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Follow Tippo
                  </p>
                  <div className="mt-3 flex justify-center">
                    <SocialIconRow
                      iconSize={28}
                      className="justify-center gap-2"
                    />
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
